import { useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { LocationConfigDTO } from '@/shared/api/model';
import { useLocationConfigStore } from '../stores/location-config-store';
import { showSuccessNotification, showErrorNotification } from '@/components/common/utils/notify';
import { useTheme } from '@/components/common/ThemeProvider';
import { AxiosError } from 'axios';

// Import Orval generated API client
import {
    useGetLocationConfig,
    useGetLocationConfigsByEntityType,
    useGetAllLocationConfigs,
    useGetAllLocationConfigsIncludingInactive,
    useCreateLocationConfig,
    useUpdateLocationConfig,
    getLocationConfigsByEntityType,
    getLocationConfig,
    getAllLocationConfigsIncludingInactive
} from '@/shared/api/client/location-configuration';

export const useLocationConfig = () => {
    const queryClient = useQueryClient();
    const { dark } = useTheme();
    
    const {
        configs,
        selectedConfig,
        isLoading,
        error,
        configForm,
        isEditMode,
        
        setConfigs,
        setSelectedConfig,
        setIsLoading,
        setError,
        setConfigForm,
        setIsEditMode,
        resetConfigForm
    } = useLocationConfigStore();

    // Get all configs
    const { 
        data: allConfigsData, 
        isLoading: isLoadingConfigs,
        refetch: refetchAllConfigs 
    } = useGetAllLocationConfigs({
        query: {
            enabled: false, // Don't fetch on mount, use explicit refetch
            // @ts-ignore
            onError: (error: AxiosError) => {
                console.error('Error fetching all location configurations:', error);
                setError('Failed to load location configurations');
                showErrorNotification('Error', 'Failed to load location configurations', dark);
            }
        }
    });

    // Error handler function
    const handleError = useCallback((message: string, error: AxiosError) => {
        console.error(`Error ${message}:`, error);
        setError(`Failed to ${message}`);
        showErrorNotification('Error', `Failed to ${message}`, dark);
    }, [dark, setError]);

    // Get configs by entity type
    const fetchConfigsByEntityType = useCallback(async (entityType: string, includeInactive: boolean = false) => {
        setIsLoading(true);
        try {
            let response;
            if (includeInactive) {
                response = await getAllLocationConfigsIncludingInactive();
            } else if (entityType === 'ALL') {
                // Use the useGetAllLocationConfigs hook results if already fetched
                if (allConfigsData) {
                    setConfigs(allConfigsData);
                    setIsLoading(false);
                    return;
                }
                
                // Otherwise refetch
                await refetchAllConfigs();
                if (allConfigsData) {
                    setConfigs(allConfigsData);
                }
            } else {
                response = await getLocationConfigsByEntityType(entityType);
                if (response) {
                    setConfigs(response);
                }
            }
        } catch (err: any) {
            handleError('fetch location configurations', err as AxiosError);
        } finally {
            setIsLoading(false);
        }
    }, [allConfigsData, refetchAllConfigs, setConfigs, setIsLoading, handleError]);

    // Update store when configs data changes
    useEffect(() => {
        if (allConfigsData) {
            setConfigs(allConfigsData);
        }
    }, [allConfigsData, setConfigs]);

    // Get a specific config by name
    const fetchConfigByName = useCallback(async (configName: string) => {
        setIsLoading(true);
        try {
            const response = await getLocationConfig(configName);
            if (response) {
                setSelectedConfig(response);
                setConfigForm({
                    configName: response.configName,
                    entityType: response.entityType,
                    addressType: response.addressType,
                    configJson: response.configJson,
                    isActive: response.isActive,
                    isMandatory: response.isMandatory
                });
                setIsEditMode(true);
            }
        } catch (err: any) {
            handleError(`fetch config ${configName}`, err as AxiosError);
        } finally {
            setIsLoading(false);
        }
    }, [setSelectedConfig, setConfigForm, setIsLoading, setIsEditMode, handleError]);

    // Create new config
    const { mutate: createConfigMutation, isPending: isCreating } = useCreateLocationConfig({
        mutation: {
            onSuccess: (data) => {
                setSelectedConfig(data);
                showSuccessNotification('Success', 'Location configuration created successfully', dark);
                resetConfigForm();
                
                // Invalidate queries
                queryClient.invalidateQueries({ queryKey: ['/api/v1/location-config'] });
                queryClient.invalidateQueries({ 
                    queryKey: ['/api/v1/location-config/entity', configForm.entityType] 
                });
            },
            onError: (error: AxiosError) => {
                handleError('create location config', error);
            }
        }
    });

    const createConfig = useCallback(() => {
        createConfigMutation({ data: configForm as LocationConfigDTO });
    }, [createConfigMutation, configForm]);

    // Update existing config
    const { mutate: updateConfigMutation, isPending: isUpdating } = useUpdateLocationConfig({
        mutation: {
            onSuccess: (data) => {
                setSelectedConfig(data);
                showSuccessNotification('Success', 'Location configuration updated successfully', dark);
                resetConfigForm();
                
                // Invalidate queries
                queryClient.invalidateQueries({ queryKey: ['/api/v1/location-config'] });
                queryClient.invalidateQueries({ 
                    queryKey: ['/api/v1/location-config', configForm.configName] 
                });
                queryClient.invalidateQueries({ 
                    queryKey: ['/api/v1/location-config/entity', configForm.entityType] 
                });
                
                // Force a refetch of the config to get the latest data
                setTimeout(() => {
                    fetchConfigsByEntityType(configForm.entityType);
                }, 100);
            },
            onError: (error: AxiosError) => {
                handleError('update location config', error);
            }
        }
    });

    const updateConfig = useCallback(() => {
        if (!configForm.configName) {
            showErrorNotification('Error', 'Configuration name is required', dark);
            return;
        }
        
        updateConfigMutation({
            configName: configForm.configName,
            data: configForm as LocationConfigDTO
        });
    }, [updateConfigMutation, configForm, dark]);

    // Save config (create or update)
    const saveConfig = useCallback(() => {
        if (isEditMode) {
            updateConfig();
        } else {
            createConfig();
        }
    }, [isEditMode, updateConfig, createConfig]);

    // Update overall loading state
    useEffect(() => {
        setIsLoading(isLoadingConfigs || isCreating || isUpdating);
    }, [isLoadingConfigs, isCreating, isUpdating, setIsLoading]);

    return {
        configs,
        selectedConfig,
        isLoading,
        error,
        configForm,
        isEditMode,
        
        setConfigForm,
        setIsEditMode,
        fetchConfigsByEntityType,
        fetchConfigByName,
        createConfig,
        updateConfig,
        saveConfig,
        resetConfigForm,
        refetchAllConfigs
    };
};

export default useLocationConfig;
